<template>
  <div class="expense-services gd-scope">
    <form autocomplete="off" class="es-search" @submit.prevent.stop v-if="!isFinished">
      <div class="es-search-source form-group">
        <select class="form-select" v-model="filterBySchedule">
          <option value="schedule">Da agenda</option>
          <option value="insurance">Da tabela do convênio</option>
          <option value="other_expense">Outras despesas</option>
        </select>
      </div>
      <div class="es-search-input form-group"
           :class="!canSearch ? 'tooltip' : ''"
           :data-tooltip="!canSearch ? 'Convênio e profissional devem estar selecionados' : ''">
        <dx-autocomplete
          v-model="expense"
          :source="findExpense"
          label="name"
          track-by="id"
          :disabled="!canSearch"
          :debounce="800"
          @select="addAppointmentExpense"
          placeholder="Informe o código ou nome da consulta, exame ou outros..."
          input-id="expense-name"
        >
          <template v-slot="{ item }">
            <a>
              <fa-icon :icon="['fal', 'file-medical-alt']" class="text-primary"/>
              <span class="ml-1">
                {{ item.expense.code | tuss }} - {{ item.expense.name }}
              </span>
            </a>
          </template>
          <template v-slot:action>
            <button type="button"
                    @click="openExpenseModal"
                    class="btn btn-action input-group-btn btn-icon btn-neutral"
                    :class="{loading: expenseModal.loading}"
                    tabindex="-1"
                    :disabled="!canSearch || expenseModal.loading">
              <fa-icon :icon="['fal', 'search']"/>
            </button>
          </template>
        </dx-autocomplete>
      </div>
    </form>

    <div class="loading loading-lg" v-if="loadingExpenses"></div>

    <div class="empty es-empty" v-else-if="serviceItems.length === 0">
      <div class="empty-icon"><fa-icon :icon="['fal', 'info-circle']" size="3x"/></div>
      <p class="empty-title h5">Serviços, taxas e outras despesas</p>
      <p class="empty-subtitle">
        Nenhum serviço neste agendamento. Informe o código ou nome no campo acima.
      </p>
    </div>

    <div class="es-list" v-else>
      <div class="es-head" :class="{'es-dual': insuranceBillingEnabled}">
        <div>Serviço</div>
        <div>Cobrar de</div>
        <div class="text-center">Qtde</div>
        <div class="text-right" v-if="insuranceBillingEnabled">Convênio</div>
        <div class="text-right">{{ insuranceBillingEnabled ? 'Paciente' : 'Valor' }}</div>
        <div></div>
      </div>
      <div class="es-body">
      <div class="es-row" v-for="item in sortedItems" :key="item.key"
           :class="[`es-row-${rowState(item)}`,
                    {locked: item.locked, 'es-dual': insuranceBillingEnabled}]">
        <div class="es-service">
          <div class="es-name" :title="item.name">{{ item.name }}</div>
          <div class="es-meta">
            <span class="es-meta-type">{{ item.typeName }} ({{ item.code | tuss }})</span>
            <template v-if="insuranceBillingEnabled">
              <guide-status-badge v-if="item.guideId" :status="item.billingStatus"
                                  :text="guideStatusText(item)"/>
              <span class="gd-pill gd-pill-paid" v-else-if="item.status === 'paid'">
                <fa-icon :icon="['fal', 'check-circle']"/>Pago no caixa
              </span>
              <span class="es-unpriced" v-if="isUnpriced(item)">
                Sem preço na tabela do convênio
              </span>
              <span class="es-unpriced text-error" v-else-if="hasPricingError(item)">
                Falha ao calcular
              </span>
            </template>
            <span class="es-state" v-else-if="item.status !== 'scheduled'">
              <fa-icon :icon="['fal', statusIcon(item.status)]"/>{{ statusName(item.status) }}
            </span>
            <span class="es-meta-action">
              <a href="#" @click.prevent="toggleReturn(item)"
                 :class="{'es-return': item.return}"
                 :title="returnHint(item)"
                 v-if="item.type === 'consultation' && !item.locked && !isFinished">
                <fa-icon :icon="['fal', 'check']" v-if="item.return"/>
                {{ item.return ? 'Retorno' : 'É retorno?' }}
              </a>
              <span class="es-return" v-else-if="item.return">
                <fa-icon :icon="['fal', 'check']"/>Retorno
              </span>
              <a href="#" @click.prevent="getPreparation(item.source.expense.id)"
                 v-if="item.type !== 'consultation'">
                <fa-icon :icon="['fal', 'info-circle']"/>Preparo
              </a>
            </span>
          </div>
          <div class="es-copay" :class="{ok: item.copay.paid}"
               v-if="insuranceBillingEnabled && item.copay.value > 0">
            Coparticipação {{ item.copay.value | currency }} ·
            {{ item.copay.paid ? 'paga no caixa' : 'a receber no caixa' }}
          </div>
        </div>
        <div class="es-payer">
          <select class="form-select" v-if="!item.locked && !isFinished"
                  :value="item.payerId" @change="changePayer(item, $event.target.value)">
            <option v-for="payer in payers" :value="payer.id" :key="payer.id">
              {{ payer.name }}
            </option>
          </select>
          <div class="es-payer-locked" v-else>
            <fa-icon :icon="['fal', payerOf(item).particular ? 'user' : 'shield-check']"/>
            {{ payerOf(item).name }}
          </div>
        </div>
        <div class="es-qty">
          <st-input-number-step v-if="!item.locked && !isFinished && item.type !== 'consultation'"
                                v-model="item.source.quantity"
                                @input="getExpensePrice(item.source)"
                                :min-value="1" :max-value="20"/>
          <span class="es-qty-locked" :class="{'es-qty-fixed': item.type === 'consultation'}"
                v-else>x{{ item.quantity }}</span>
        </div>
        <template v-if="insuranceBillingEnabled">
          <div class="es-value">
            <span class="loading" v-if="isPricingItem(item)"></span>
            <span class="gd-muted" v-else-if="payerOf(item).particular">—</span>
            <span class="text-gray" v-else-if="item.return">R$ 0,00</span>
            <template v-else>{{ insuranceTotal(item) | currency }}</template>
          </div>
          <div class="es-value">
            <span class="loading" v-if="isPricingItem(item)"></span>
            <template v-else-if="particularTotal(item) > 0">
              {{ particularTotal(item) | currency }}
            </template>
            <span class="gd-muted" v-else>—</span>
          </div>
        </template>
        <div class="es-value" v-else>
          <span class="loading" v-if="isPricingItem(item)"></span>
          <template v-else-if="item.return">
            <span class="text-gray">R$ 0,00</span>
          </template>
          <template v-else>
            {{ itemTotal(item) | currency }}
            <small v-if="!payerOf(item).particular">convênio</small>
          </template>
        </div>
        <div class="es-actions">
          <template v-if="insuranceBillingEnabled && item.guideId">
            <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                    :data-tooltip="lockTooltip(item)"
                    :disabled="item.billingStatus !== 'open' || isFinished"
                    @click="openGuideModal({ accountId: item.guideId })">
              <fa-icon :icon="['fal', 'pencil']"/>
            </button>
            <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                    data-tooltip="Imprimir guia"
                    :disabled="printing" @click="printGuide(item.guideId)">
              <fa-icon :icon="['fal', 'print']"/>
            </button>
          </template>
          <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                  data-tooltip="Imprimir guia" :disabled="printing"
                  v-else-if="item.status === 'guide'"
                  @click="printAccount(item.source.accountId)">
            <fa-icon :icon="['fal', 'print']"/>
          </button>
          <template v-else-if="item.status === 'paid' || item.copay.paid">
            <button class="btn btn-sm btn-action btn-icon btn-gray tooltip-left"
                    data-tooltip="Nota fiscal"
                    :class="{
                      loading: invoiceLoadingFor(item),
                      tooltip: !invoiceLoadingFor(item),
                    }"
                    :disabled="fiscalDocument.loading || printing"
                    v-if="canInvoice(item)"
                    @click="openEntryInvoice(item)">
              <fa-icon :icon="['fal', 'file-invoice-dollar']"/>
            </button>
            <button class="btn btn-sm btn-action btn-icon btn-gray tooltip-left"
                    data-tooltip="Recibo"
                    :class="{
                      loading: printingReceiptFor(item),
                      tooltip: !printingReceiptFor(item),
                    }"
                    :disabled="fiscalDocument.loading || printing"
                    @click="printEntryReceipt(item)">
              <fa-icon :icon="['fal', 'print']"/>
            </button>
          </template>
          <template v-else-if="!isFinished && !item.locked">
            <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                    data-tooltip="Recalcular preço"
                    v-if="insuranceBillingEnabled && needsPrice(item)"
                    :disabled="isPricingItem(item)" @click="getExpensePrice(item.source)">
              <fa-icon :icon="['fal', 'redo']"/>
            </button>
            <button class="btn btn-sm btn-action btn-icon btn-error tooltip tooltip-left"
                    data-tooltip="Remover" @click="removeItem(item)">
              <fa-icon :icon="['fal', 'times']"/>
            </button>
          </template>
        </div>
      </div>
      </div>

      <div class="es-footer">
        <div class="es-total" v-if="insuranceBillingEnabled">
          Convênio <strong>{{ totalInsurance | currency }}</strong>
          Paciente <strong>{{ totalParticular | currency }}</strong>
        </div>
        <div class="es-total" v-else>
          Total <strong>{{ total | currency }}</strong>
        </div>
        <div class="es-footer-actions">
          <span class="es-pricing-hint" v-if="repricingAll">Recalculando preços…</span>
          <span class="es-pending-hint" v-else-if="pendingText">{{ pendingText }}</span>
          <button class="btn btn-secondary btn-icon btn-icon-left"
                  :class="{loading: registering}"
                  :disabled="isFinished || registering || isPricing || repricingAll"
                  @click="$emit('register')">
            <fa-icon :icon="['fal', 'file-medical-alt']"/>
            Gerenciar atendimentos
          </button>
        </div>
      </div>
    </div>

    <dx-modal
      title="Lista de serviços, taxas e outras despesas"
      v-model="expenseModal.show" size="lg"
      v-if="expenseModal.show"
      id="modal-expenses-appointment"
    >
      <div class="loading loading-lg mt-2" v-if="expenseModal.loading"/>
      <template v-else>
        <div class="columns">
          <div class="column col-3 form-group">
            <select class="form-select" v-model="filterBySchedule" @change="searchExpenseModal">
              <option value="schedule">Da agenda</option>
              <option value="insurance">Da tabela do convênio</option>
              <option value="other_expense">Outras despesas</option>
            </select>
          </div>
          <div class="column col-9 form-group">
            <div class="input-group">
              <input type="text"
                     class="form-input"
                     id="search"
                     v-model="expenseModal.search"
                     v-focus
                     @keydown.enter="searchExpenseModal"
                     placeholder="Digite parte do nome ou código e tecle [Enter]">
              <button class="btn btn-neutral btn-icon btn-action input-group-btn"
                      :disabled="expenseModal.loading"
                      :class="{loading: expenseModal.loading}"
                      @click="searchExpenseModal">
                <fa-icon :icon="['fal', 'search']"/>
              </button>
            </div>
          </div>
        </div>
        <div class="empty mt-2"
             v-if="expenseModal.data.items.length === 0 && !expenseModal.loading">
          <div class="empty-icon">
            <fa-icon :icon="['fal', 'info-circle']" size="2x"/>
          </div>
          <p class="empty-title h6">Serviços, taxas e outras despesas</p>
          <p class="empty-subtitle">
            Nenhum serviço encontrado para esta agenda. Verifique as configurações.
          </p>
        </div>
        <table class="table table-hover" v-else>
          <thead>
          <tr>
            <th style="width: 85px">Código</th>
            <th>Nome</th>
            <th class="text-right">Valor Paciente</th>
          </tr>
          </thead>
          <tbody>
          <tr class="c-hand" v-for="(item, i) in expenseModal.data.items" :key="i"
              @click="selectExpense(item)">
            <td>
              <span v-if="item.code">{{ item.code | tuss }}</span>
              <span v-else>-</span>
            </td>
            <td>{{ item.expense.name | capitalizeWord }}</td>
            <td class="text-right">
              {{ item.definition.particularValue | currency }}
            </td>
          </tr>
          </tbody>
        </table>
        <div class="load-more" v-if="expenseModal.data.items.length > 0">
          <button v-if="expenseModal.data.hasMore"
                  class="btn btn-primary"
                  @click="loadMore"
                  :disabled="expenseModal.loadingMore"
                  :class="{loading: expenseModal.loadingMore}">Carregar mais...</button>
        </div>
      </template>
      <template slot="footer">
        <button class="btn" @click="expenseModal.show = false">Sair</button>
      </template>
    </dx-modal>

    <dx-modal title="Informações de preparo"
              v-model="preparationModal.show" size="lg"
              v-if="preparationModal.show"
              id="modal-preparation">
      <div class="preparation-content" v-html="preparation"/>
      <template slot="footer">
        <button class="btn btn-gray mr-1"
                :class="{loading: preparationModal.printing}"
                :disabled="preparationModal.printing"
                @click="printPreparation">
          <fa-icon :icon="['fal', 'print']"/>
          Imprimir
        </button>
        <button class="btn" @click="preparationModal.show = false">Sair</button>
      </template>
    </dx-modal>

    <referral-modal
      v-if="account.show"
      :show="account.show"
      :data="account.data"
      :source="source"
      :type="account.type"
      @close="hideAccountModal"
    />

    <guide-modal
      v-if="guide.show"
      :show="guide.show"
      :account-id="guide.accountId"
      :type="guide.type"
      :appointment="appointmentForGuide()"
      :insurance="guide.insurance"
      :preselected-expense-ids="guide.preselectedExpenseIds"
      source="appointment"
      @saved="guideSaved"
      @deleted="guideDeleted"
      @close="closeGuideModal"
    />

    <fiscal-document
      v-if="fiscalDocument.show"
      :show="fiscalDocument.show"
      :entity="fiscalDocument.entity"
      :total-amount="fiscalDocument.totalAmount"
      :origin-type="fiscalDocument.origin.type"
      :origin-id="fiscalDocument.origin.id"
      @loading="fiscalDocument.loading = $event"
      @close="closeFiscalDocument"
    />
  </div>
</template>

<script>
import moment from 'moment';
import { mapMutations } from 'vuex';
import { resolvePrices } from '@/app/billing/guide/service';
import GuideStatusBadge from '@/app/billing/guide/components/GuideStatusBadge.vue';
import expenseServices from '../../mixins/expenseServices';

const OTHER_EXPENSE_TYPES = [
  'daily',
  'gas',
  'material',
  'medicine',
  'others',
  'service',
  'surgery',
  'tax',
];

export default {
  mixins: [expenseServices],
  components: {
    GuideStatusBadge,
  },
  props: {
    registering: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expense: null,
      filterBySchedule: 'schedule',
      loadingExpenses: false,
      pricing: [],
      pricingState: {},
      repricingAll: false,
      expenseModal: {
        loading: false,
        loadingMore: false,
        show: false,
        search: '',
        data: {
          items: [],
          hasMore: false,
          limit: 30,
          offset: 0,
        },
      },
      preparationModal: {
        show: false,
        printing: false,
        expenseId: '',
        preparation: '',
      },
      printingEntryId: null,
    };
  },
  computed: {
    canSearch() {
      return !!(this.form.insurance.plan.id && this.form.professional.id);
    },
    preparation() {
      if (!this.preparationModal.preparation) return '';
      return this.preparationModal.preparation
        .replace(/(<figure([^>]+)>)/ig, '')
        .replace(/<\/figure>/ig, '')
        .replace(/<table>/ig, '<table class="table">');
    },
    sortedItems() {
      const rank = item => (item.status === 'scheduled' ? 1 : 0);
      return [...this.serviceItems].sort((a, b) => rank(a) - rank(b));
    },
    total() {
      return this.serviceItems.reduce((sum, item) => sum + this.itemTotal(item), 0);
    },
    totalInsurance() {
      return this.serviceItems.reduce((sum, item) => sum + this.insuranceTotal(item), 0);
    },
    totalParticular() {
      return this.serviceItems.reduce((sum, item) => sum + this.particularTotal(item), 0);
    },
    pendingText() {
      const scheduled = this.serviceItems.filter(item => item.status === 'scheduled');
      if (!this.insuranceBillingEnabled) {
        if (scheduled.length === 0) return '';
        return `${scheduled.length} ${scheduled.length === 1 ? 'item pendente' : 'itens pendentes'}`;
      }
      const withoutGuide = scheduled.filter(item => !this.payerOf(item).particular).length;
      const toReceive = scheduled.filter(item => this.payerOf(item).particular).length
        + this.serviceItems.filter(item => item.copay.pending).length;
      const parts = [];
      if (withoutGuide > 0) parts.push(`${withoutGuide} sem guia`);
      if (toReceive > 0) parts.push(`${toReceive} a receber no caixa`);
      return parts.join(' · ');
    },
    professionalId() {
      return this.form.professional ? this.form.professional.id : null;
    },
    planId() {
      return this.form.insurance && this.form.insurance.plan ? this.form.insurance.plan.id : null;
    },
    isPricing() {
      return this.pricing.length > 0;
    },
  },
  watch: {
    isPricing(value) {
      this.$emit('pricing', value);
    },
    professionalId(value, previous) {
      if (value && previous && this.insuranceBillingEnabled) this.repriceAll();
    },
    planId(value, previous) {
      if (value && previous && this.insuranceBillingEnabled) this.repriceAll();
    },
    'form.id': function formIdChanged(value) {
      if (value) this.loadGuides();
    },
  },
  mounted() {
    this.loadGuides();
  },
  methods: {
    isPricingItem(item) {
      return this.pricing.includes(item.source);
    },
    rowState(item) {
      if (!this.insuranceBillingEnabled) return item.status;
      if (item.status === 'guide') return item.billingStatus === 'closed' ? 'paid' : 'guide';
      return item.status;
    },
    pricingStateOf(item) {
      return this.pricingState[item.key] || null;
    },
    isUnpriced(item) {
      const state = this.pricingStateOf(item);
      return !!(state && state.priced === false && !state.error)
        && item.status === 'scheduled' && !item.return;
    },
    needsPrice(item) {
      if (this.isUnpriced(item) || this.hasPricingError(item)) return true;
      return this.payerOf(item).particular && item.status === 'scheduled' && !item.return
        && this.particularTotal(item) === 0;
    },
    hasPricingError(item) {
      const state = this.pricingStateOf(item);
      return !!(state && state.error) && item.status === 'scheduled';
    },
    resolveItemPrice(item) {
      const insurance = item.insurance && item.insurance.plan && item.insurance.plan.id
        ? item.insurance
        : this.form.insurance;
      const key = item.id || item.key || String(this.form.expenses.indexOf(item));
      const request = {
        key,
        expenseId: item.expense.id,
        insuranceId: insurance.id,
        planId: insurance.plan.id,
        professionalId: item.professional && item.professional.id
          ? item.professional.id
          : this.form.professional.id,
        specialtyCode: item.expense.specialty && item.expense.specialty.code
          ? item.expense.specialty.code
          : undefined,
        quantity: item.quantity || 1,
        factor: item.factor || 1,
        return: !!item.return,
      };
      this.pricing.push(item);
      return resolvePrices([request])
        .then(([price]) => {
          if (!price) return;
          const totals = price.totals || {};
          item.unitInsurance = price.unit ? price.unit.insurance || 0 : 0;
          item.unitParticular = price.unit ? price.unit.particular || 0 : 0;
          item.totalInsurance = totals.insurance || 0;
          item.totalParticular = totals.particular || 0;
          this.$set(this.pricingState, key, { priced: !!price.priced, error: false });
        })
        .catch(() => {
          this.$set(this.pricingState, key, { priced: false, error: true });
        })
        .finally(() => {
          this.pricing = this.pricing.filter(pricingItem => pricingItem !== item);
        });
    },
    async repriceAll() {
      const items = this.serviceItems.filter(item => !item.locked).map(item => item.source);
      if (items.length === 0) return;
      this.repricingAll = true;
      try {
        await Promise.all(items.map(item => this.resolveItemPrice(item)));
        const payer = this.payerOf(this.serviceItems[0]);
        this.$toast.success(`Preços recalculados para ${payer.name}.`);
      } finally {
        this.repricingAll = false;
      }
    },
    entryTotal(entryId) {
      return this.serviceItems
        .filter(item => item.source.cashRegisterEntryId === entryId)
        .reduce((sum, item) => sum + this.itemTotal(item), 0);
    },
    canInvoice(item) {
      return this.isFiscalDocumentEnabled
        && this.payerOf(item).particular
        && this.entryTotal(item.source.cashRegisterEntryId) > 0;
    },
    openEntryInvoice(item) {
      const entryId = item.source.cashRegisterEntryId;
      this.openFiscalDocument('cash_register_entry', entryId, this.entryTotal(entryId));
    },
    invoiceLoadingFor(item) {
      return this.fiscalDocument.loading
        && !!this.fiscalDocument.origin
        && this.fiscalDocument.origin.id === item.source.cashRegisterEntryId;
    },
    printingReceiptFor(item) {
      return !!this.printingEntryId
        && this.printingEntryId === item.source.cashRegisterEntryId;
    },
    async printEntryReceipt(item) {
      this.printingEntryId = item.source.cashRegisterEntryId;
      await this.printReceipt(this.printingEntryId);
      this.printingEntryId = null;
    },
    ...mapMutations({
      addExpense: 'Appointment.ADD_EXPENSE',
      deleteExpense: 'Appointment.DELETE_EXPENSE',
    }),
    searchParams(search) {
      const params = {
        insuranceId: this.form.insurance.id,
        planId: this.form.insurance.plan.id,
        date: moment(this.form.startDate).format('YYYY-MM-DD'),
      };
      if (search) params.search = search;
      let url = `/professionals/${this.form.professional.id}/expense-prices`;
      if (this.filterBySchedule === 'schedule') {
        params.scheduleId = this.form.schedule.id;
      } else if (this.filterBySchedule === 'other_expense') {
        params.types = OTHER_EXPENSE_TYPES.join(',');
        url = '/expense-insurances';
      }
      return { url, params };
    },
    findExpense(search) {
      const { url, params } = this.searchParams(search);
      return this.$httpX
        .get(url, { params: { ...params, limit: 30, offset: 0 } })
        .then(({ data }) => {
          if (data.items.length === 0) {
            return [{
              id: null,
              diagnostic: true,
              search,
              expense: {
                id: null,
                type: null,
                name: 'Serviço não encontrado neste convênio. Clique para ver por quê',
              },
              quantity: 0,
              factor: 1,
              definition: {
                insuranceValue: 0,
                particularValue: 0,
              },
            }];
          }
          return data.items;
        });
    },
    openDiagnostic(search) {
      this.$store.commit('Appointment.SHOW_DIAGNOSTIC_MODAL', {
        scheduleId: this.form.schedule && this.form.schedule.id
          ? this.form.schedule.id
          : this.form.scheduleId,
        date: moment(this.form.startDate).format('YYYY-MM-DD'),
        planId: this.form.insurance.plan.id,
        search,
      });
    },
    async addAppointmentExpense(expense, quantityToAdd = 1) {
      if (expense && expense.diagnostic) {
        this.openDiagnostic(expense.search);
        return;
      }

      if (!expense || expense.expense.id === null) {
        return;
      }

      this.loadingExpenses = true;

      const { insuranceValue, particularValue, secondSegment } = expense.definition;
      let totalInsurance = insuranceValue;
      let totalParticular = particularValue;

      const found = this.form.expenses.find(item => item.expense.id === expense.expense.id);
      const quantity = expense.expense.type === 'consultation'
        ? 1
        : (found ? found.quantity : 0) + quantityToAdd;

      if (quantity > 1) {
        const percent = (secondSegment || 100) / 100;
        const remaining = quantity - 1;
        totalInsurance = insuranceValue + insuranceValue * remaining * percent;
        totalParticular = particularValue + particularValue * remaining * percent;
      }

      if (found) {
        found.quantity = quantity;
        found.totalInsurance = totalInsurance;
        found.totalParticular = totalParticular;
      } else {
        this.addExpense({
          id: null,
          isNew: true,
          selected: true,
          accountId: null,
          cashRegisterEntryId: null,
          paid: false,
          insurance: this.resolveInsurance(this.form.insurance),
          expense: {
            id: expense.expense.id,
            code: expense.code,
            type: expense.expense.type,
            name: expense.expense.name,
            specialty: expense.expense.specialty || null,
          },
          return: false,
          quantity,
          factor: 1,
          totalInsurance,
          totalParticular,
        });
      }

      if (this.insuranceBillingEnabled) {
        const target = found || this.form.expenses[this.form.expenses.length - 1];
        await this.resolveItemPrice(target);
      } else if ((totalInsurance + totalParticular) === 0) {
        this.$toast.show('Atenção! Serviço ainda não precificado.', { type: 'error' });
      }

      this.$emit('validateScheduleInfo');

      if (expense.hasPreparation) {
        this.getPreparation(expense.expense.id);
      }

      if (expense.hasAggregates) {
        await this.addAggregates(expense.expense.id);
      }

      this.loadingExpenses = false;
      this.expense = null;
    },
    async addAggregates(id) {
      const params = {
        insuranceId: this.form.insurance.id,
        planId: this.form.insurance.plan.id,
        professionalId: this.form.professional.id,
      };
      await this.$http.get(`/expenses/${id}/aggregates`, { params })
        .then(({ data }) => {
          data.items.forEach((item) => {
            this.addAppointmentExpense(item, item.quantity || 1);
          });
        })
        .catch(() => {
          this.$toast.show('Erro ao carregar agregados.', { type: 'error' });
        });
    },
    getExpensePrice(item) {
      if (this.insuranceBillingEnabled) return this.resolveItemPrice(item);
      const insurance = item.insurance && item.insurance.plan && item.insurance.plan.id
        ? item.insurance
        : this.form.insurance;
      const params = {
        insuranceId: insurance.id,
        planId: insurance.plan.id,
        expenseId: item.expense.id,
      };
      this.pricing.push(item);
      return this.$httpX
        .get(`/professionals/${this.form.professional.id}/expense-prices`, { params })
        .finally(() => {
          this.pricing = this.pricing.filter(pricingItem => pricingItem !== item);
        })
        .then(({ data }) => {
          if (data && data.items.length > 0) {
            const { insuranceValue, particularValue, secondSegment } = data.items[0].definition;
            if (item.quantity === 1) {
              item.totalInsurance = insuranceValue;
              item.totalParticular = particularValue;
            } else {
              const percent = (secondSegment || 100) / 100;
              const remaining = item.quantity - 1;
              item.totalInsurance = insuranceValue + insuranceValue * percent * remaining;
              item.totalParticular = particularValue + particularValue * percent * remaining;
            }
          }
        })
        .catch(() => {});
    },
    changePayer(item, payerId) {
      this.setPayer(item, payerId);
      this.getExpensePrice(item.source);
      this.$emit('validateScheduleInfo');
    },
    returnHint(item) {
      return item.return
        ? 'Retorno, sem cobrança. Clique para cobrar como consulta'
        : 'Clique se esta consulta é um retorno, sem cobrança';
    },
    toggleReturn(item) {
      item.source.return = !item.source.return;
      this.$emit('validateScheduleInfo');
    },
    removeItem(item) {
      this.deleteExpense(item.source);
      this.$emit('validateScheduleInfo');
    },
    openExpenseModal() {
      this.expenseModal.search = '';
      this.searchExpenseModal();
      this.expenseModal.show = true;
    },
    searchExpenseModal() {
      this.expenseModal.data.items = [];
      this.expenseModal.data.hasMore = false;
      this.expenseModal.data.offset = 0;
      this.loadExpenseModalItems();
    },
    async loadExpenseModalItems() {
      const firstData = this.expenseModal.data.offset === 0;
      if (firstData) {
        this.expenseModal.loading = true;
      } else {
        this.expenseModal.loadingMore = true;
      }

      const { url, params } = this.searchParams(this.expenseModal.search);
      params.limit = this.expenseModal.data.limit;
      params.offset = this.expenseModal.data.offset;

      this.$httpX.get(url, { params })
        .then(({ data }) => {
          this.expenseModal.data.hasMore = data.hasMore;
          this.expenseModal.data.items = firstData
            ? data.items
            : [...this.expenseModal.data.items, ...data.items];
        })
        .catch(() => {})
        .finally(() => {
          this.expenseModal.loading = false;
          this.expenseModal.loadingMore = false;
        });
    },
    async loadMore() {
      this.expenseModal.data.offset += this.expenseModal.data.limit;
      await this.loadExpenseModalItems();
    },
    selectExpense(item) {
      this.addAppointmentExpense(item);
      this.expenseModal.show = false;
    },
    getPreparation(id) {
      this.$httpX.get(`/expenses/${id}`)
        .then(({ data }) => {
          if (data.preparation) {
            this.preparationModal.expenseId = id;
            this.preparationModal.preparation = data.preparation;
            this.preparationModal.show = true;
          } else {
            this.$toast.show('Serviço sem preparo cadastrado', { type: 'error' });
          }
        });
    },
    printPreparation() {
      this.preparationModal.printing = true;
      return this.$file
        .print(`/expenses/${this.preparationModal.expenseId}/print-preparation`)
        .catch(() => {})
        .finally(() => {
          this.preparationModal.printing = false;
        });
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/_variables.scss";
@import "../../../billing/guide/guide.scss";
.expense-services {
  .es-search {
    display: flex;
    gap: $layout-spacing;
    margin: $layout-spacing 0;
    .es-search-source {
      flex: 0 0 13rem;
      margin-bottom: 0;
    }
    .es-search-input {
      flex: 1 1 auto;
      margin-bottom: 0;
    }
  }
  .es-empty {
    background-color: $gray-color-ultra-light;
    border: $border-color solid $border-width;
    border-radius: $border-radius;
  }
  .es-list {
    border: $border-color-dark solid $border-width;
    border-radius: $border-radius;
    margin-bottom: $layout-spacing-lg;
    overflow: hidden;
  }
  .es-head, .es-row {
    align-items: center;
    display: grid;
    gap: $layout-spacing;
    grid-template-columns: minmax(0, 1fr) 176px 92px 90px 60px;
    padding: 0 $layout-spacing;
    &.es-dual {
      grid-template-columns: minmax(0, 1fr) 176px 92px 90px 90px 60px;
    }
  }
  @media (max-width: 840px) {
    .es-head.es-dual, .es-row.es-dual {
      grid-template-columns: minmax(0, 1fr) 120px 72px 90px 90px 60px;
    }
  }
  .es-head {
    background-color: $gray-color-ultra-light;
    border-bottom: $border-color-dark solid $border-width;
    color: $gray-color-dark;
    font-size: $font-size-sm;
    font-weight: 600;
    padding: .35rem calc(#{$layout-spacing} * 2 + 1px) .35rem calc(#{$layout-spacing} * 2 + 4px);
  }
  .es-body {
    display: flex;
    flex-direction: column;
    gap: .45rem;
    padding: $layout-spacing;
  }
  .es-row {
    background-color: $light-color;
    border: $border-color solid $border-width;
    border-left: 4px solid $warning-color;
    border-radius: $border-radius;
    padding-bottom: .55rem;
    padding-top: .55rem;
    transition: background-color .15s ease, border-color .15s ease;
    &:hover {
      background-color: $gray-color-ultra-light;
      border-color: $border-color-dark;
      border-left-color: $warning-color;
    }
    &.es-row-scheduled {
      background-color: #FFF8E6;
      border-color: #F2C94C;
      border-left-color: $warning-color;
      border-style: dashed;
      border-width: 2px;
      border-left-style: solid;
      border-left-width: 4px;
      &:hover {
        background-color: darken(#FFF8E6, 3%);
        border-color: darken(#F2C94C, 8%);
        border-left-color: $warning-color;
      }
    }
    &.es-row-guide {
      background-color: lighten($info-color, 48%);
      border-color: lighten($info-color, 34%);
      border-left-color: $info-color;
      &:hover {
        background-color: lighten($info-color, 45%);
        border-color: lighten($info-color, 28%);
        border-left-color: $info-color;
      }
    }
    &.es-row-paid, &.es-row-courtesy {
      background-color: #F2FAF5;
      border-color: #BFE3CD;
      border-left-color: #2FA36B;
      &:hover {
        background-color: #E3F4EA;
        border-color: #A9D8BB;
        border-left-color: #2FA36B;
      }
    }
    &:hover .es-name {
      overflow: visible;
      white-space: normal;
    }
  }
  .es-copay {
    color: darken($warning-color, 22%);
    font-size: $font-size-xs;
    font-weight: 600;
    margin-top: .15rem;
    &.ok {
      color: #1F7A4E;
    }
  }
  .es-unpriced {
    color: darken($warning-color, 22%);
    font-weight: 600;
  }
  .es-pricing-hint {
    color: $gray-color-dark;
    font-size: $font-size-sm;
  }
  .es-service {
    min-width: 0;
  }
  .es-name {
    font-weight: 600;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .es-state {
    align-items: center;
    background-color: $light-color;
    border: $border-color-dark solid $border-width;
    border-radius: 1rem;
    color: $gray-color-dark;
    display: inline-flex;
    flex: 0 0 auto;
    font-size: $font-size-xs;
    font-weight: 600;
    line-height: 1;
    padding: .2rem .45rem;
    white-space: nowrap;
    svg {
      margin-right: .25rem;
    }
  }
  .es-row-guide .es-state {
    border-color: lighten($info-color, 22%);
    color: darken($info-color, 10%);
  }
  .es-row-paid .es-state, .es-row-courtesy .es-state {
    border-color: #2FA36B;
    color: #1F7A4E;
  }
  .es-meta {
    align-items: center;
    color: $gray-color-dark;
    display: flex;
    flex-wrap: wrap;
    font-size: $font-size-xs;
    gap: .3rem .6rem;
    margin-top: .15rem;
    white-space: nowrap;
    .es-meta-type {
      flex: 0 1 auto;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .es-meta-action {
      display: flex;
      flex: 0 0 auto;
      gap: .5rem;
    }
    a {
      font-weight: 500;
      svg {
        margin-right: .2rem;
      }
      &:focus, &:active {
        box-shadow: none;
        outline: none;
      }
    }
  }
  .es-return {
    color: darken($warning-color, 22%);
    font-weight: 600;
    svg {
      margin-right: .2rem;
    }
  }
  .es-payer-locked {
    color: $gray-color-dark;
    font-size: $font-size-sm;
    svg {
      margin-right: .3rem;
    }
  }
  .es-qty {
    text-align: center;
    .input-group {
      justify-content: center;
      .btn {
        flex: 0 0 1.4rem;
        padding: 0;
        width: 1.4rem;
      }
      .form-input {
        flex: 0 0 2rem;
        padding: 0;
        width: 2rem;
      }
    }
    .es-qty-locked {
      color: $gray-color-dark;
      &.es-qty-fixed {
        color: $dark-color;
        font-weight: 600;
      }
    }
  }
  .es-value {
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    text-align: right;
    .loading {
      display: inline-block;
      min-height: 1rem;
      min-width: 1rem;
    }
    small {
      color: $gray-color-dark;
      display: block;
      font-size: $font-size-xs;
      font-weight: 400;
    }
  }
  .es-actions {
    text-align: right;
    white-space: nowrap;
    .btn + .btn {
      margin-left: .2rem;
    }
  }
  .es-footer {
    align-items: center;
    background-color: $gray-color-ultra-light;
    border-top: $border-color-dark solid $border-width;
    display: flex;
    gap: $layout-spacing-lg;
    justify-content: space-between;
    padding: $layout-spacing;
  }
  .es-total {
    color: $gray-color-dark;
    font-size: $font-size-sm;
    strong {
      color: $dark-color;
      font-size: $font-size;
      font-variant-numeric: tabular-nums;
      margin-left: .3rem;
    }
  }
  .es-footer-actions {
    align-items: center;
    display: flex;
    gap: $layout-spacing;
  }
  .es-pending-hint {
    color: darken($warning-color, 22%);
    font-size: $font-size-sm;
    font-weight: 600;
  }
}

#modal-expenses-appointment {
  .load-more {
    margin: $layout-spacing-lg 0;
    text-align: center;
    .btn {
      padding: 0 $layout-spacing-xl;
    }
  }
}

#modal-preparation {
  .preparation-content {
    background-color: $gray-color-ultra-light;
    border: $border-width solid $border-color;
    border-radius: $border-radius;
    padding: $layout-spacing-lg;
    p {
      margin-bottom: $layout-spacing;
    }
  }
}
</style>
